// ============================================================
//  src/components/ErrorBoundary.jsx
//
//  Catches any unhandled render/lifecycle error in the tree
//  and shows a friendly recovery screen instead of a blank page.
//
//  Usage (in App.jsx, wrapping the whole tree):
//    import ErrorBoundary from "./components/ErrorBoundary";
//    <ErrorBoundary>
//      <App />
//    </ErrorBoundary>
// ============================================================

import { Component } from "react";
import config from "../config";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console in dev; swap for a real error-reporting service
    // (e.g. Sentry) in production.
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "32px 20px",
        textAlign: "center",
        background: config?.colors?.background || "#faf8f5",
      }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>⚠️</div>
        <div style={{
          fontSize: 22, fontWeight: 800,
          color: config?.colors?.primary || "#2d5a27",
          marginBottom: 8,
        }}>
          Something went wrong
        </div>
        <div style={{
          fontSize: 14, color: "#888",
          maxWidth: 320, lineHeight: 1.6, marginBottom: 24,
        }}>
          An unexpected error occurred. Please refresh the page to continue shopping.
        </div>

        {/* Show error detail in development only */}
        {process.env.NODE_ENV === "development" && this.state.error && (
          <pre style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 10,
            padding: "12px 16px",
            fontSize: 11,
            color: "#dc2626",
            maxWidth: "90vw",
            overflowX: "auto",
            textAlign: "left",
            marginBottom: 20,
          }}>
            {this.state.error.toString()}
          </pre>
        )}

        <button
          onClick={() => window.location.reload()}
          style={{
            background: config?.colors?.primary || "#2d5a27",
            color: "#fff",
            border: "none",
            borderRadius: 13,
            padding: "13px 28px",
            fontWeight: 800,
            fontSize: 15,
            cursor: "pointer",
          }}
        >
          🔄 Refresh Page
        </button>
      </div>
    );
  }
}
